import Button from "components/Elements/Button/Button.js";
import { Toggle } from "components/Elements/Toggle/Toggle.js";
import React, { useState, useRef, useEffect, useMemo } from "react";
import { useContent } from "contexts/contentContext/contentContext.js";
import Tooltip from "components/Elements/Tooltip/Tooltip.js";
import Icon from "components/Elements/Icon/Icon.tsx";
import apiRequest from "utils/ApiRequest/apiRequest.js";
import Image from "components/Elements/Image/Image.tsx";
import { useUser } from "contexts/userContext/userContext.js";
import { useToasts } from "react-toast-notifications";
import { useModal } from "contexts/modalContext/modalContext.js";
import { LinkToNewTab } from "components/Elements/Link/Link.js";
import { useCookie } from "contexts/cookieContext/cookieContext.js";
import { defaultFormImages } from "components/Reusable/JUMBOform/ExternalJsonsList/JsonLists.js";
import EmbedLimitOptionsModal from "components/Reusable/JUMBOform/Modals/EmbedLimitOptionsModal.js";
import CreateEmbedPageModalNew from "components/Pages/ProfileUser/JumboEmbed/Modals/CreateEmbedPageModalNew.js";
import { urlRegex } from "helpers/validators.js";
import getAsyncImage from "utils/getAsyncImage";
import config from "config";
import Recaptcha from "components/Reusable/Recaptcha/Recaptcha.tsx";
import { awaiter } from "components/Pages/Home/Helpers/helpers.js";
import { RECAPTCHA_APPEAR_DELAY } from "utils/globalConsts.ts";

const PublishForm = ({
  EmbedDynamicId,
  formWidth,
  formHeight,
  websiteURL,
  setWebsiteURL,
  invalidURL,
  setInvalidURL,
  setUpdateBeforeSave,
  userEmbedPage,
  setUserEmbedPage,
  embedDynamicPageId,
  notExistEmbedPage,
  setDisplayOnEmbedPage,
  displayOnEmbedPage,
  displaySelectIntegrateTooltip,
  setDisplaySelectIntegrateTooltip,
  formMaxHeight,
  setHideSavedText,
}) => {
  const { setModal, closeModal } = useModal();
  const { getUserData, isPremium } = useUser();
  const { addToast } = useToasts();

  const userData = getUserData();
  const {
    getTextByKey,
    getDirection,
    getLanguageCulture,
    getLanguage,
    getBasicSettingsData,
  } = useContent();
  //const [confirmTerms, setConfirmTerms] = useState(false);
  const [scriptOrIframe, setScriptOrIframe] = useState("script");
  const [exampleScript, setExampleScript] = useState("");
  const [loadingForms, setLoadingForms] = useState(false);
  const [dynamicFormsList, setDynamicFormsList] = useState([]);
  const [numOfForms, setNumOfForms] = useState(0);
  const textAreaRef = useRef(null);
  const [displayToggleEmbedCode, setDisplayToggleEmbedCode] = useState(false);
  const { getCookie, setCookies, removeCookies } = useCookie();
  const [scriptIsCopied, setScriptIsCopied] = useState(false);
  const [focuseWebsiteUrl, setFocuseWebsiteUrl] = useState(null);
  const [blurWebsiteUrl, setBlurWebsiteUrl] = useState(null);

  const [isCaptchaShown, setIsCaptchaShown] = useState(false);
  const [captchaToken, setCaptchaToken] = useState("");
  const recaptchaRef = useRef();
  const [submitData, setSubmitData] = useState({});
  useEffect(() => {
    if (captchaToken) {
      createEmbedPage(submitData);

      setCaptchaToken(null);
      setIsCaptchaShown(false);
    }
  }, [captchaToken, submitData]);

  useEffect(() => {
    if (blurWebsiteUrl !== focuseWebsiteUrl) {
      const validURL = new RegExp(urlRegex);
      if (validURL.test(websiteURL)) {
        setInvalidURL(false);
        setUpdateBeforeSave({ mode: true, action: "updtateWebsiteUrl" });
      } else {
        setInvalidURL(true);
      }
    }
  }, [blurWebsiteUrl]);

  useEffect(() => {
    if (EmbedDynamicId > 0) {
      const checkIfConfirm = getCookie(`confirmTerms-${EmbedDynamicId}`);
      if (checkIfConfirm && checkIfConfirm !== undefined) {
        //setConfirmTerms(true);
        setDisplayToggleEmbedCode(checkIfConfirm === "true" ? true : false);
      }
    }
  }, [EmbedDynamicId]);

  const displayFormOnEmbedPageFunction = () => {
    //if set to false do not do validations
    if (displayOnEmbedPage) {
      if (EmbedDynamicId > 0) {
        if (embedDynamicPageId) {
          const displayForm = !displayOnEmbedPage;
          updateEmbedForm(displayForm);
          setDisplayOnEmbedPage(displayForm);
        } else {
          addToast(getTextByKey("need-create-page"), {
            appearance: "error",
          });
        }
      }
    } else {
      if (isPremium() || numOfForms < 2) {
        if (EmbedDynamicId > 0) {
          if (embedDynamicPageId) {
            const displayForm = !displayOnEmbedPage;
            updateEmbedForm(displayForm);
            setDisplayOnEmbedPage(displayForm);
          } else {
            addToast(getTextByKey("need-create-page"), {
              appearance: "error",
            });
          }
        }
      } else {
        setModal(() => <EmbedLimitOptionsModal />);
      }
    }
  };

  useEffect(() => {
    if (EmbedDynamicId > 0) {
      getExampleScript("script");
    }
  }, [EmbedDynamicId]);

  //get script/iframe code
  const getExampleScript = async (type) => {
    setScriptOrIframe(type);
    const parameters = {
      typeScript: type,
      culture: getLanguageCulture(),
      userId: userData.UserId,
      serviceType: "embed",
      formId: EmbedDynamicId,
      formWidth: formWidth,
      formHeight: formMaxHeight > 0 ? formMaxHeight : formHeight,
    };
    const response = await apiRequest(
      "EmbedDynamics",
      "CreateEmbedDynamicCodeReact",
      parameters
    );
    if (response.status === "success") {
      setExampleScript(response.data);
    }
  };
  //Copy the textarea value
  const copyTextAreaToClibord = () => {
    setScriptIsCopied(true);
    textAreaRef.current.select();
    document.execCommand("copy");
    // This is just personal preference.
    // I prefer to not show the the whole text area selected.
  };

  const updateEmbedForm = async (displayForm) => {
    setHideSavedText(false);
    setUpdateBeforeSave({
      mode: true,
      action: "DisplayEmbedFormOnEmbedPageFirst",
    });
    if (EmbedDynamicId > 0) {
      const body = {
        Embed_Dynamic_Id: EmbedDynamicId,
        EmbedPageId: embedDynamicPageId,
        DisplayFormOnEmbedPage: displayForm,
      };
      const response = await apiRequest(
        "EmbedDynamics",
        "DisplayEmbedFormOnEmbedPage",
        body,
        "POST"
      );
      if (response.status === "success") {
        if (response.data > 0) {
          //show error message
          addToast(getTextByKey("error-add-form-to-page"), {
            appearance: "error",
          });
        } else {
          setUpdateBeforeSave({
            mode: true,
            action: "DisplayEmbedFormOnEmbedPageSecond",
          });
        }
      }
    }
  };

  const createEmbedPage = async ({
    companyName,
    userNameUrl,
    activeFormLists,
  }) => {
    if (!captchaToken) {
      setIsCaptchaShown(true);
      setSubmitData({ companyName, activeFormLists });
      if (getBasicSettingsData()?.recaptchaType === "google") {
        await awaiter(RECAPTCHA_APPEAR_DELAY);
        await recaptchaRef.current.executeAsync();
      }
      return;
    }

    const body = {
      EmbedDynamicPageUsername: userData.UserId,
      Occupation: "",
      EmbedDynamicPageCompanyName: companyName.trim(),
      EmbedDynamicPageUserId: userData.UserId,
      EmbedDynamicPageActive: true,
      EmbedDynamicPageDirection: getDirection(),
      EmbedPageCulture: getLanguageCulture(),
      ShortCulture: getLanguage(),
      EmbedDynamicPageFormIdsList: activeFormLists,
      isProfilePages: true,
    };
    const response = await apiRequest(
      "EmbedDynamics",
      "CreateEmbedPageReact",
      body,
      "POST"
    );
    if (response.status === "success") {
      if (response.data) {
        if (response.data.EmbedDybamicPageResponseCode === 0) {
          addToast(getTextByKey("create-embed-page-success"), {
            appearance: "success",
          });

          //referesh page
          window.location.reload();
        } else if (response.data.EmbedDybamicPageResponseCode === 460017) {
          addToast(getTextByKey("embed-subscription-expired"), {
            appearance: "error",
          });
        } else {
          addToast(getTextByKey("global-error-toast"), {
            appearance: "error",
          });
        }
      }
    }
    closeModal();
  };

  const getNumOfEmbedForms = async () => {
    const response = await apiRequest(
      "EmbedDynamics",
      "GetNumOfActiveEmbedDynamicForms"
    );
    if (response.status === "success") {
      if (response.data) {
        setNumOfForms(response.data);
      }
    }
  };

  useEffect(() => {
    if (EmbedDynamicId > 0 && notExistEmbedPage) {
      getAllEmbedDynamicForms(userData.UserId);
    }
  }, [EmbedDynamicId, notExistEmbedPage]);

  const handleDisplayEmbedCode = () => {
    if (isPremium()) {
      if (EmbedDynamicId > 0) {
        const checkIfConfirm = getCookie(`confirmTerms-${EmbedDynamicId}`);
        if (checkIfConfirm !== undefined) {
          removeCookies(`confirmTerms-${EmbedDynamicId}`);
        }
        const confirmTermsCookie = `confirmTerms-` + EmbedDynamicId;
        setCookies(confirmTermsCookie, !displayToggleEmbedCode, {
          path: "/",
          domain: ".jumbomail.me",
        });
        setDisplayToggleEmbedCode((prev) => !prev);
      } else {
        // addToast(getTextByKey("before-save-form"), {
        //   appearance: "error",
        // });
      }
    } else {
      setModal(() => <EmbedLimitOptionsModal />);
    }
  };

  useEffect(() => {
    if (scriptIsCopied) {
      setTimeout(() => {
        setScriptIsCopied(false);
      }, 3000);
    }
  }, [scriptIsCopied]);

  ///Get all embed Forms
  const getAllEmbedDynamicForms = async (id) => {
    setLoadingForms(true);
    const parameters = {
      userId: id,
      includePickerForm: false,
    };
    const response = await apiRequest(
      "EmbedDynamics",
      "GetAllEmbedFormsListReact",
      parameters
    );
    if (response.status === "success") {
      let tempList = [];
      if (response.data !== null) {
        if (response.data.length > 0) {
          for (let i = 0; i < response.data.length; i++) {
            const element = response.data[i];
            const callBackImage = (image) => {
              element.Embed_Dynamic_Image = image;
              tempList.push(element);
            };
            if (element.Embed_Dynamic_Image_Url) {
              if (
                element.Embed_Dynamic_Image_Url.includes(
                  `${config.api.resource}/assets/images/picker/picker-app.png`
                )
              ) {
                callBackImage(element.Embed_Dynamic_Image_Url);
              } else {
                if (!element.Embed_Dynamic_Image_Url.includes("/")) {
                  await getAsyncImage(
                    `images/personal/form-images/webp/${element.Embed_Dynamic_Image_Url}.webp`,
                    callBackImage
                  );
                }
              }
            } else {
              if (element.Embed_Dynamic_Image) {
                tempList.push(element);
              } else {
                await getAsyncImage(
                  `images/${defaultFormImages[i]}.webp`,
                  callBackImage
                );
              }
            }
          }
          setDynamicFormsList(tempList);
        }
      }
    }
    setLoadingForms(false);
  };

  const handleChangeWebsiteUrl = (e) => {
    setWebsiteURL(e);
    setInvalidURL(false);
  };

  useMemo(() => {
    if (userData && userData.UserId) {
      getNumOfEmbedForms();
    }
  }, [userData]);

  return (
    <div className="publish-container">
      {isCaptchaShown ? (
        <Recaptcha
          recaptchaRef={recaptchaRef}
          onSuccess={(token) => setCaptchaToken(token)}
        />
      ) : null}
      <div className="publish-title">
        {getTextByKey("create-form-integrate")}
      </div>
      <div className="publish-content">
        <div className="embed-personal-page-section">
          {!notExistEmbedPage && (
            <div className="toggle-div">
              <Toggle
                status={displayOnEmbedPage}
                setStatus={displayFormOnEmbedPageFunction}
              />
              <span>{getTextByKey("display-form-on-embed-page")}</span>
            </div>
          )}
          <div className="display-form-description">
            <p>{getTextByKey("display-form-on-embed-page-text")}</p>
          </div>
          <div className="create-page-button">
            {userEmbedPage ? (
              <LinkToNewTab
                page={`page-edit/${userEmbedPage.EmbedPageUserName}`}
                isNewTab={true}
              >
                <Button className="new-button embed-button">
                  {getTextByKey("go-to-my-page-button")}
                </Button>
              </LinkToNewTab>
            ) : (
              <div
                onClick={() =>
                  setModal(() => (
                    <CreateEmbedPageModalNew
                      createPage={createEmbedPage}
                      dynamicFormsList={dynamicFormsList}
                    />
                  ))
                }
              >
                <Button className="new-button embed-button">
                  {getTextByKey("create-page")}
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="my-url">
          <div className="toggle-div">
            <Toggle
              status={isPremium() ? displayToggleEmbedCode : false}
              setStatus={handleDisplayEmbedCode}
            />
            <span>{getTextByKey("get-embed-code")}</span>
          </div>

          {displayToggleEmbedCode ? (
            <>
              <div className="url-website-label">
                <span className="url-title-span">
                  {getTextByKey("my-url-address")}
                </span>
                <Tooltip
                  id={"my-url"}
                  tooltipTextKey="my-url-address-tooltip"
                  //hardPositioned
                  end
                >
                  <Icon name="question-circle" iconClass="tooltip-question" />
                </Tooltip>
              </div>
              <div
                className={`url-website-input ${invalidURL ? "invalid" : ""} `}
              >
                <input
                  value={websiteURL}
                  onChange={(e) => handleChangeWebsiteUrl(e.target.value)}
                  onFocus={(e) => setFocuseWebsiteUrl(e.target.value)}
                  onBlur={(e) => setBlurWebsiteUrl(e.target.value)}
                  type="text"
                />
              </div>
              {invalidURL ? (
                <div className="valid">
                  {getTextByKey("invalid-url-address")}
                </div>
              ) : null}
              <div className="form-script">
                <p>{getTextByKey("form-code-text1")}</p>
                <p>{getTextByKey("form-code-text2")}</p>
                <div className="get-links">
                  <div className="link-type">
                    <span
                      onClick={() => getExampleScript("script")}
                      className={scriptOrIframe === "script" ? "active" : ""}
                    >
                      Script
                    </span>{" "}
                    <span
                      onClick={() => getExampleScript("iframe")}
                      className={scriptOrIframe === "iframe" ? "active" : ""}
                    >
                      Iframe
                    </span>
                  </div>
                  <div
                    className="copy-icon"
                    onClick={() => copyTextAreaToClibord()}
                  >
                    <span
                      className={`copy-text ${
                        scriptIsCopied ? "display-text" : ""
                      }`}
                    >
                      {getTextByKey("copied")}
                    </span>
                    <Icon
                      title={getTextByKey("jumboforms-copy-code")}
                      name="copy-b"
                    />
                  </div>
                </div>
                <div className="script-line">
                  <div className="script-code">
                    <textarea
                      readOnly={true}
                      ref={textAreaRef}
                      value={exampleScript}
                    ></textarea>
                  </div>
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>
      {displaySelectIntegrateTooltip && (
        <div className="tooltip-select-one">
          <div className="select-one-container">
            <div className="select-one-title-and-text">
              <div className="select-one-title">
                <div className="exclamation-icon">
                  <Image
                    name="create-form/exclamation-icon"
                    ext="svg"
                    alt=""
                    title=""
                  />
                </div>
                <span className="select-integrate-title-span">
                  {getTextByKey("select-integrate-title")}
                </span>
              </div>
              <div className="select-one-content">
                <p>{getTextByKey("select-integrate-text")}</p>
              </div>
            </div>
            <div
              className="exist-div"
              onClick={() => setDisplaySelectIntegrateTooltip(false)}
            >
              <Icon name={"x"} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PublishForm;
